/**
 * @file orders.tsx
 * @module app/modules/sales-management/sales-representative/orders/orders
 * @description Implements the orders interface for sales-management operations.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  FormLabel,
  OverlayTrigger,
  Popover,
  Row,
} from "react-bootstrap";
import APICallService from "../../../../../api/apiCallService";
import { assistAdmin, ordersDelivery } from "../../../../../api/apiEndPoints";
import {
  Manager,
  ORDER_PAGE_LIMIT,
  SalesRepOrders,
  ServiceMember,
  OrdersDelivery,
  View,
  OrderPlacedByCustomer,
  RouteOrderMovedToPendingOrder,
  OrderSubmitted,
  OrderProcessed,
  OrderOutForDelivery,
  OrderDelivered,
  OrderCancelled,
  AssistAdmin,
  AllCustomers,
  ActivationManager,
  ActivationRep,
} from "../../../../../utils/constants";
import { getKey, setKey } from "../../../../../Global/history";
import { listSalesRepOrdersDelivery } from "../../../../../utils/storeString";
import { useAuth } from "../../../auth";
import Loader from "../../../../../Global/loader";
import OrderPagination from "../../../../../Global/order-pagination";
import { useNavigate } from "react-router-dom";
import Method from "../../../../../utils/methods";
import { IMAGES } from "../../../../../utils/assetsAndPermissions";
import { KTSVG } from "../../../../../_admin/helpers";
import CustomDatePicker from "../../../../custom/DateRange/CustomDatePicker";
import { CustomSelect } from "../../../../custom/Select/CustomSelect";
import { orderStatusJSON } from "../../../../../utils/staticJSON";
import { useNavigationHistory } from "../../../../../utils/useNavigationHistory";

const Orders = () => {
  const { currentUser, saveCurrentUser }: any = useAuth();
  const [currentUserData, setCurrentUserData] = useState<any>();
  const navigate = useNavigate();
  const { navigateTo } = useNavigationHistory();
  const kgToTons = (kilograms: any) => {
    return (kilograms / 1000).toFixed(3);
  };
  const getStatusBadge = (status: any) => {
    const statusObj = orderStatusJSON.find((val) => val.value === status);
    if (!statusObj)
      return (
        <div className="badge rounded-pill bg-light-secondary text-secondary fs-14 p-4">
          NA
        </div>
      );

    let badgeClass = "bg-light-primary text-primary";
    switch (status) {
      case OrderProcessed:
        badgeClass = "bg-light-warning text-warning";
        break;
      case OrderOutForDelivery:
        badgeClass = "bg-light-info text-info";
        break;
      case OrderDelivered:
        badgeClass = "bg-light-success text-success";
        break;
      case OrderCancelled:
        badgeClass = "bg-light-danger text-danger";
        break;
      default:
        badgeClass = "bg-light-primary text-primary";
    }

    return (
      <div className={`badge rounded-pill ${badgeClass} fs-14 py-2 px-4`}>
        {statusObj.label}
      </div>
    );
  };
  const popoverPartialOrder = (
    <Popover id="popover-basic">
      <Popover.Body>This order is partially processed.</Popover.Body>
    </Popover>
  );
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState<any>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [fetchLoader, setFetchLoader] = useState(true);
  const [page, setPage] = useState<any>(
    getKey(listSalesRepOrdersDelivery.page) || 1
  );
  const [pageLimit, setPageLimit] = useState<any>(
    getKey(listSalesRepOrdersDelivery.limit) || ORDER_PAGE_LIMIT
  );
  const [status, setStatus] = useState<any>(
    getKey(listSalesRepOrdersDelivery.status)
      ? Number(getKey(listSalesRepOrdersDelivery.status))
      : null
  );
  const [district, setDistrict] = useState<any>(
    currentUser &&
    currentUser.districts &&
    currentUser.districts[0] &&
    currentUser.districts.find((val: any) => val.flag === true)?.reference
  );
  const [startDate, setStartDate] = useState<any>(
    getKey(listSalesRepOrdersDelivery.filterStartDate)
      ? new Date(
        getKey(listSalesRepOrdersDelivery.filterStartDate)?.toString() || ""
      )
      : null
  );
  const [endDate, setEndDate] = useState<any>(
    getKey(listSalesRepOrdersDelivery.filterEndDate)
      ? new Date(
        getKey(listSalesRepOrdersDelivery.filterEndDate)?.toString() || ""
      )
      : null
  );
  const [search, setSearch] = useState<any>(
    getKey(listSalesRepOrdersDelivery.search) || ""
  );
  useEffect(() => {
    (async () => {
      setFetchLoader(true);
      const profileData = await fetchProfileData();
      let start = startDate;
      let end = endDate;
      const isValidDate = (d: any) => d && !isNaN(new Date(d).getTime());
      // If there's no valid start/end from persisted keys, use profile target dates (if available)
      if (!isValidDate(start) && !isValidDate(end) && isValidDate(profileData?.currentSalesTarget?.startDate) && isValidDate(profileData?.currentSalesTarget?.endDate)) {
        start = new Date(profileData.currentSalesTarget.startDate);
        end = new Date(profileData.currentSalesTarget.endDate);
        setStartDate(start);
        setEndDate(end);
        setKey(listSalesRepOrdersDelivery.filterStartDate, start, false);
        setKey(listSalesRepOrdersDelivery.filterEndDate, end, false);
      }
      await fetchOrdersList(
        page,
        pageLimit,
        status ? [status] : [],
        search,
        start,
        end,
        district
      );
      setFetchLoader(false);
    })();
  }, []);

  const fetchOrdersList = async (
    pageNo: number,
    limit: number,
    statusList?: any,
    search?: string,
    fromDate?: string,
    toDate?: string,
    district?: any
  ) => {
    setLoading(true);
    let params: any = {
      pageNo: pageNo,
      limit: limit,
      sortKey: "_createdAt",
      sortOrder: -1,
      needCount: true,
      searchTerm: search ? search : "",
      fromDate: fromDate ? Method.convertDateToFormat(fromDate, "YYYY-MM-DD") : "",
      toDate: toDate ? Method.convertDateToFormat(toDate, "YYYY-MM-DD") : "",
      // district: district && district !== -1 ? district : "",
      "assignedTo[0]": currentUser?._id
    };
    if (statusList && statusList.length > 0) {
      statusList.map((val: any) => {
        params = { ...params, listType: val };
      });
    }
    let endpoint = ordersDelivery.listAssistAdmin;
    //  if (
    //    currentUser?.role.some(
    //      (obj: any) => obj.type === Manager || obj.type === ServiceMember
    //    )
    //  ) {
    //    endpoint = ordersDelivery.listAssistAdmin;
    //  }
    let apiService = new APICallService(
      endpoint,
      params,
      "",
      "",
      "",
      "",
      SalesRepOrders
    );
    // Always call API with the provided params. Server will interpret missing from/to appropriately.
    let response: any = await apiService.callAPI();
    if (response && response.records) {
      setTotalRecords(response.total || 0);
      setOrders(response.records);
    } else {
      setTotalRecords(0);
      setOrders([]);
    }
    setLoading(false);
  };
  const fetchProfileData = async () => {
    let repId = currentUser?._id;

    let apiService = new APICallService(
      assistAdmin.info,
      '',
      { id: repId },
      "",
      "",
      "",
      AllCustomers,
      ""
    );
    let response = await apiService.callAPI();
    if (response) {
      const data = JSON.parse(JSON.stringify(response));
      setCurrentUserData(data);
      return data;
    }
    return null;
  };
  const handleCurrentPage = async (val: number) => {
    setFetchLoader(true);
    setPage(val);
    setKey(listSalesRepOrdersDelivery.page, val);
    await fetchOrdersList(
      val,
      pageLimit,
      status ? [status] : [],
      search,
      startDate,
      endDate,
      district
    );
    setFetchLoader(false);
  };
  const handleNextPage = async (val: number) => {
    setFetchLoader(true);
    setPage(val + 1);
    setKey(listSalesRepOrdersDelivery.page, val + 1);
    await fetchOrdersList(
      val + 1,
      pageLimit,
      status ? [status] : [],
      search,
      startDate,
      endDate,
      district
    );
    setFetchLoader(false);
  };
  const handlePreviousPage = async (val: number) => {
    setFetchLoader(true);
    setPage(val - 1);
    setKey(listSalesRepOrdersDelivery.page, val - 1);
    await fetchOrdersList(
      val - 1,
      pageLimit,
      status ? [status] : [],
      search,
      startDate,
      endDate,
      district
    );
    setFetchLoader(false);
  };
  const handlePageLimit = async (event: any) => {
    const limit = parseInt(event.target.value);
    setFetchLoader(true);
    setPage(1);
    setPageLimit(limit);
    setKey(listSalesRepOrdersDelivery.page, 1);
    setKey(listSalesRepOrdersDelivery.limit, limit);
    await fetchOrdersList(
      1,
      limit,
      status ? [status] : [],
      search,
      startDate,
      endDate,
      district
    );
    setFetchLoader(false);
  };
  const handleDateFilter = async (event: any) => {
    let start = event && event[0] ? event[0] : null;
    let end = event && event[1] ? event[1] : null;

    if (!start && !end && currentUserData?.currentSalesTarget?.startDate && currentUserData?.currentSalesTarget?.endDate) {
      start = new Date(currentUserData.currentSalesTarget.startDate);
      end = new Date(currentUserData.currentSalesTarget.endDate);
    }

    setStartDate(start);
    setEndDate(end);
    setKey(listSalesRepOrdersDelivery.filterStartDate, start, false);
    setKey(listSalesRepOrdersDelivery.filterEndDate, end, false);

    if (start && end) {
      setFetchLoader(true);
      setPage(1);
      setKey(listSalesRepOrdersDelivery.page, 1);
      await fetchOrdersList(
        1,
        pageLimit,
        status ? [status] : [],
        search,
        start,
        end,
        district
      );
      setFetchLoader(false);
    }
  };
  const handleSearch = async (value: any) => {
    setSearch(value);
    setFetchLoader(true);
    setPage(1);
    setKey(listSalesRepOrdersDelivery.search, value, false);
    setKey(listSalesRepOrdersDelivery.page, 1);
    await fetchOrdersList(
      1,
      pageLimit,
      status ? [status] : [],
      value,
      startDate,
      endDate,
      district
    );
    setFetchLoader(false);
  };
  const handleStatusFilter = async (value: any) => {
    setStatus(value);
    setFetchLoader(true);
    setKey(listSalesRepOrdersDelivery.status, value);
    setKey(listSalesRepOrdersDelivery.page, 1);
    setLoading(true);
    setPage(1);
    await fetchOrdersList(
      1,
      pageLimit,
      value ? [value] : [],
      search,
      startDate,
      endDate,
      district
    );
    setFetchLoader(false);
  };
  return (
    <div className="p-5">
      <Row className="align-items-center mb-7 g-5">
        <Col xs>
          <div className="d-flex justify-content-between">
            <h1 className="fs-22 fw-bolder">Orders</h1>
            <Button
              variant="primary"
              className="uw-btn uw-btn-primary uw-btn-lg fs-16 fw-bold btn-lg"
              onClick={() => {
                currentUser?.userType === AssistAdmin
                  ? currentUser?.role.some(
                    (obj: any) => obj.type === Manager || obj.type === ServiceMember || obj.type === ActivationManager || obj.type === ActivationRep
                  )
                    ? navigate("/orders/add-new-order-assistAdmin")
                    : navigate("/orders/add-new-order")
                  : navigate("/orders/add-new-order");
                setKey(listSalesRepOrdersDelivery.search, search, false);
                setKey(listSalesRepOrdersDelivery.filterStartDate, startDate, false);
                setKey(listSalesRepOrdersDelivery.filterEndDate, endDate, false);
              }}
            >
              Place Order
            </Button>
          </div>
          <Card className="bg-light border my-7">
            <Card.Body className="px-7">
              <Row className="align-items-center gy-5">
                <Col sm={12} md={6} lg={4}>
                  <FormLabel className="fs-16 fw-500">
                    Search by Order Id / Customer Name
                  </FormLabel>
                  <div className="d-flex align-items-center position-relative">
                    <KTSVG
                      path="/media/icons/duotune/general/gen021.svg"
                      className="svg-icon-3 position-absolute ms-3"
                    />
                    <input
                      type="text"
                      id="kt_filter_search"
                      className="form-control form-control-custom borde-r8px bg-white ps-11"
                      placeholder="Search"
                      value={search}
                      onChange={(event: any) => {
                        handleSearch(event.target.value);
                      }}
                    />
                  </div>
                </Col>
                <Col sm={12} md={6} lg={4}>
                  <FormLabel className="fs-16 fw-500">
                    Filter by status
                  </FormLabel>
                  <div>
                    <CustomSelect
                      backgroundColor="#ffff"
                      value={orderStatusJSON.filter(option => option.value !== 3).find(
                        (option) => option?.value == status
                      )}
                      options={orderStatusJSON.filter(option => option.value !== 3)}
                      onChange={(event: any) =>
                        handleStatusFilter(event ? event.value : undefined)
                      }
                      isSearchable={false}
                      isMulti={false}
                      isClearable={true}
                      placeholder="Select"
                    />
                  </div>
                </Col>
                <Col sm={12} md={6} lg={4}>
                  <FormLabel className="fs-16 fw-500">Filter by date</FormLabel>
                  <CustomDatePicker
                    className="form-control bg-white min-h-60px fs-16 fw-bold text-dark min-w-175px"
                    onChange={handleDateFilter}
                    selectsRange={true}
                    startDate={startDate}
                    endDate={endDate}
                    dateFormat="dd/MM/yyyy"
                    showFullMonthYearPicker={true}
                    maxDate={
                      currentUserData?.currentSalesTarget?.endDate
                        ? new Date(currentUserData?.currentSalesTarget?.endDate)
                        : new Date()
                    }
                    minDate={
                      currentUserData?.currentSalesTarget?.startDate
                        ? new Date(
                          currentUserData?.currentSalesTarget?.startDate
                        )
                        : undefined
                    }
                    isClearable={true}
                    inputTextBG="bg-white"
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>
          {!fetchLoader ? (
            orders && orders.length ? (
              <>
                <Card className="border border-r10px">
                  <Card.Body className="p-0">
                    <div className="table-responsive">
                      <table className="table table-rounded table-row-bordered align-middle gs-7 gy-6 mb-0">
                        <thead>
                          <tr className="fw-bold fs-16 fw-600 text-dark border-bottom align-middle">
                            {/* {enableSelectAll ? (
                                                          <th
                                                            className="min-w-100px"
                                                            tabIndex={0}
                                                            rowSpan={1}
                                                            colSpan={1}
                                                          >
                                                            <div className="form-check form-check-sm form-check-custom form-check-solid">
                                                              <input
                                                                className="form-check-input h-30px w-30px"
                                                                type="checkbox"
                                                                value="1"
                                                                checked={
                                                                  selectLoading ||
                                                                  (selectedId.length >=
                                                                    totalRecords &&
                                                                    districtData.findIndex(
                                                                      (
                                                                        val: any,
                                                                        index: any
                                                                      ) => {
                                                                        return (
                                                                          val._id ==
                                                                          currentDistrict
                                                                        );
                                                                      }
                                                                    ) === 0)
                                                                }
                                                                onChange={(
                                                                  event: any
                                                                ) => {
                                                                  handleSelectAll();
                                                                }}
                                                              />
                                                              <label className="form-check-label fs-16 fw-600 text-dark">
                                                                Select all
                                                              </label>
                                                            </div>
                                                          </th>
                                                        ) : (
                                                          <th className="min-w-md-175px min-w-150px">
                                                            Order Date & ID
                                                          </th>
                                                        )} */}
                            <th className="min-w-md-175px min-w-150px">
                              Order Date & ID
                            </th>
                            <th className="min-w-md-200px min-w-125px">
                              Customer name
                            </th>
                            {currentUser?.role.length ? (
                              currentUser?.role.some(
                                (obj: any) =>
                                  obj.type !== Manager ||
                                  obj.type !== ServiceMember
                              ) ? (
                                <></>
                              ) : (
                                <th className="min-w-140px">Ordered by</th>
                              )
                            ) : (
                              <></>
                            )}
                            {/* <th className="min-w-md-125px">Rep Name</th> */}
                            <th className="min-w-md-125px min-w-100px">
                              Total amount
                            </th>
                            <th className="min-w-md-75px min-w-50px">Status</th>
                            <th className="min-w-100px text-end"></th>
                          </tr>
                        </thead>
                        <tbody>
                          <>
                            {orders.map((orderVal: any, goodIndex: number) => (
                              <tr key={goodIndex}>
                                {/* {enableSelectAll ? (
                                                                  <td>
                                                                    <div className="form-check form-check-sm form-check-custom form-check-solid">
                                                                      <input
                                                                        className="form-check-input h-30px w-30px"
                                                                        type="checkbox"
                                                                        value={
                                                                          orderVal._id
                                                                        }
                                                                        checked={selectedId.includes(
                                                                          orderVal._id
                                                                        )}
                                                                        onChange={() => {
                                                                          handleChecked(
                                                                            orderVal._id
                                                                          );
                                                                        }}
                                                                      />
                                                                      <div className="d-flex align-items-start flex-column ms-2">
                                                                        <div className="d-flex flex-row">
                                                                          {orderVal.totalQuantities.reduce(
                                                                            (
                                                                              total: number,
                                                                              item: any
                                                                            ) =>
                                                                              total +
                                                                              item.stockCount,
                                                                            0
                                                                          ) >
                                                                            orderVal.mProcessedQuantities &&
                                                                          orderVal.mProcessedQuantities !=
                                                                            0 ? (
                                                                            <OverlayTrigger
                                                                              trigger="hover"
                                                                              placement="bottom"
                                                                              overlay={
                                                                                popoverPartialOrder
                                                                              }
                                                                            >
                                                                              <img
                                                                                className="me-2"
                                                                                width={28}
                                                                                height={
                                                                                  21
                                                                                }
                                                                                src={
                                                                                  IMAGES.PartialTruckTwo
                                                                                }
                                                                                alt=""
                                                                              />
                                                                            </OverlayTrigger>
                                                                          ) : (
                                                                            <></>
                                                                          )}
                                                                          <span className="text-dark d-block">
                                                                            {Method.convertDateToDDMMYYYYHOURS(
                                                                              orderVal._createdAt
                                                                            )}
                                                                          </span>
                                                                        </div>
                                                                        <span className="text-gray d-block">
                                                                          {
                                                                            orderVal.refKey
                                                                          }
                                                                        </span>
                                                                      </div>
                                                                    </div>
                                                                  </td>
                                                                ) : (
                                                                  <td className="fs-15 fw-500">
                                                                    <div className="d-flex align-items-start flex-column">
                                                                      <div className="d-flex flex-row">
                                                                        {orderVal.totalQuantities.reduce(
                                                                          (
                                                                            total: number,
                                                                            item: any
                                                                          ) =>
                                                                            total +
                                                                            item.stockCount,
                                                                          0
                                                                        ) >
                                                                          orderVal.mProcessedQuantities &&
                                                                        orderVal.mProcessedQuantities !=
                                                                          0 ? (
                                                                          <OverlayTrigger
                                                                            trigger="hover"
                                                                            placement="bottom-start"
                                                                            overlay={
                                                                              popoverPartialOrder
                                                                            }
                                                                          >
                                                                            <img
                                                                              className="me-2"
                                                                              width={28}
                                                                              height={21}
                                                                              src={
                                                                                IMAGES.PartialTruckTwo
                                                                              }
                                                                              alt=""
                                                                            />
                                                                          </OverlayTrigger>
                                                                        ) : (
                                                                          <></>
                                                                        )}
                                                                        <span className="text-dark d-block">
                                                                          {Method.convertDateToDDMMYYYYHOURS(
                                                                            orderVal._createdAt
                                                                          )}
                                                                        </span>
                                                                      </div>
                                                                      <span className="text-gray d-block">
                                                                        {orderVal.refKey}
                                                                      </span>
                                                                    </div>
                                                                  </td>
                                                                )} */}
                                <td className="fs-15 fw-500">
                                  <div className="d-flex align-items-start flex-column">
                                    <span className="text-dark d-block">
                                      {Method.convertDateToDDMMYYYYHOURS(
                                        orderVal._createdAt
                                      )}
                                    </span>
                                    <span className="text-gray d-block">
                                      {orderVal.refKey}
                                    </span>
                                  </div>
                                </td>
                                <td>
                                  <span className="fs-15 fw-500 d-block">
                                    {orderVal.customer.name}
                                  </span>
                                </td>
                                {/* {currentUser?.role.length ? (
                                  currentUser?.role.some(
                                    (obj: any) =>
                                      obj.type !== Manager ||
                                      obj.type !== ServiceMember
                                  ) ? (
                                    <></>
                                  ) : (
                                    <td>
                                      <span className="badge badge-light border-r23px p-4 fs-15 fw-600 text-dark">
                                        {orderVal.placedBy.type ===
                                        OrderPlacedByCustomer
                                          ? "Customer"
                                          : orderVal.placedBy.user.name}
                                      </span>
                                    </td>
                                  )
                                ) : (
                                  <></>
                                )} */}
                                {/* <td>
                                  <span className="fs-15 fw-500">
                                    {orderVal.mProcessedQuantities > 0
                                      ? orderVal.mOriginalOrderedQuantity -
                                        orderVal.mProcessedQuantities
                                      : orderVal.mOriginalOrderedQuantity}
                                    {" items"}
                                  </span>
                                </td> */}
                                {/* <td>
                                  <span className="fs-15 fw-500 badge rounded-pill bg-light-secondary text-black p-4 h-10">
                                    {orderVal?.customer?.assignedTo?.name ?? "NA"}
                                  </span>
                                </td> */}
                                <td>
                                  <span className="fs-15 fw-500">
                                    {"TSh " +
                                      Method.getGeneralizedAmount(
                                        orderVal.payment.totalCharge
                                      )}
                                  </span>
                                </td>
                                <td>{getStatusBadge(orderVal.status)}</td>
                                <td className="text-end">
                                  {Method.hasPermission(
                                    OrdersDelivery,
                                    View,
                                    currentUser
                                  ) ? (
                                    <Button
                                      className="uw-btn uw-btn-primary fs-14 fw-600"
                                      onClick={() => {
                                        let currentScrollY = window.scrollY;
                                        setKey(
                                          listSalesRepOrdersDelivery.scrollPosition,
                                          currentScrollY
                                        );
                                        setKey(
                                          listSalesRepOrdersDelivery.search,
                                          search,
                                          false
                                        );
                                        setKey(
                                          listSalesRepOrdersDelivery.filterStartDate,
                                          startDate,
                                          false
                                        );
                                        setKey(
                                          listSalesRepOrdersDelivery.filterEndDate,
                                          endDate,
                                          false
                                        );
                                        navigateTo("/orders/order-details", {
                                          state: {
                                            id: orderVal._id,
                                            moduleName: OrdersDelivery,
                                          },
                                        });
                                      }}
                                    >
                                      View details
                                    </Button>
                                  ) : (
                                    <></>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </>
                        </tbody>
                      </table>
                    </div>
                  </Card.Body>
                </Card>
                {!loading && orders && orders.length ? (
                  <OrderPagination
                    totalRecords={totalRecords}
                    currentPage={page}
                    handleCurrentPage={handleCurrentPage}
                    handleNextPage={handleNextPage}
                    handlePreviousPage={handlePreviousPage}
                    handlePageLimit={handlePageLimit}
                    pageLimit={pageLimit}
                  />
                ) : (
                  <></>
                )}
              </>
            ) : (
              <>
                {startDate && endDate ? (
                  <div className="border border-r10px mb-6">
                    <div className="d-flex justify-content-center text-center align-items-center min-h-160px px-3">
                      <span className="fs-18 fw-500">No Data Found</span>
                    </div>
                  </div>
                ) : (
                  <div className="border border-r10px mb-6">
                    <div className="d-flex justify-content-center text-center align-items-center min-h-160px px-3">
                      <span className="fs-18 fw-500">
                        New orders / Unprocessed orders will be shown in this
                        section.
                      </span>
                    </div>
                  </div>
                )}
              </>
            )
          ) : (
            <div className="border border-r10px mb-6">
              <div className="d-flex justify-content-center text-center align-items-center min-h-160px px-3">
                <Loader loading={fetchLoader || loading} />
              </div>
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};
export default Orders;
